// Este arquivo tenta ser muito genérico, o que pode levar a problemas de tipagem. Em alguns casos o eslint foi desabilitado
import { MessageBarType } from "@fluentui/react";
import { StringifiableRecord } from "query-string";
import { createContext, ReactNode, useCallback, useContext } from "react";

import { makeRequest } from "../utils";
import { FetchResponse } from "../utils/fetchData";
import { isHtmlString } from "../utils/isHtmlString";

import { AlertTypes } from "@/common/types/AlertTypes";
import { useAlert } from "@/hooks/useAlert";

interface Messages {
  success?: string;
  error: string;
}

export interface ExecuteData {
  url: string;
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  notificationMessage?: string;
  showAlert?: boolean;
  params?: StringifiableRecord;
  messages?: Messages;
  bodyObj?: object;
  preserveEmptyStrings?: boolean;
  setIsFetching?: React.Dispatch<React.SetStateAction<boolean>>;
}

interface ApiContextData {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  execute: <T = any>(data: ExecuteData) => Promise<FetchResponse<T>>;
}

const ApiContext = createContext<ApiContextData>({} as ApiContextData);

const API_URL = `${import.meta.env.VITE_API_URL}`;

export const ApiProvider = ({ children }: { children: ReactNode }) => {
  const { createAlert } = useAlert();

  const execute = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async <T = any,>({
      url,
      method = "GET",
      showAlert,
      notificationMessage,
      params,
      bodyObj,
      preserveEmptyStrings,
      messages,
      setIsFetching
    }: ExecuteData): Promise<FetchResponse<T>> => {
      const baseUrl = `${API_URL}/${url}`;

      if (setIsFetching) setIsFetching(true);

      try {
        const response = await makeRequest({
          method,
          baseUrl,
          queryParams: params,
          bodyObj,
          preserveEmptyStrings
        });

        // eslint-disable-next-line @typescript-eslint/only-throw-error
        if (response.error) throw response;

        const messageFromData =
          messages?.success ||
          notificationMessage ||
          (typeof (response.data as { message?: string })?.message === "string"
            ? (response.data as { message?: string }).message
            : undefined);

        const message = messageFromData || `Requisição ${method} em ${url}`;

        const apiResponseObject = {
          expiration: new Date().getTime(),
          createdAt: new Date().getTime(),
          timeout: 10000,
          level: MessageBarType.success,
          code: response.response.status,
          path: url,
          message,
          type: AlertTypes.API,
          method: method
        };

        if (showAlert) {
          createAlert(apiResponseObject);
        }

        return response;
      } catch (err) {
        const errorMessageFromParams = messages?.error || notificationMessage;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        const apiErrorMessage = err?.error?.message;

        let finalErrorMessage =
          errorMessageFromParams ||
          `Tivemos um problema na requisição ${method} em ${url}`;

        if (
          typeof apiErrorMessage === "string" &&
          !isHtmlString(apiErrorMessage)
        ) {
          finalErrorMessage = `${finalErrorMessage} (${apiErrorMessage})`;
        }

        if (showAlert) {
          createAlert({
            timeout: 4000,
            level: MessageBarType.error,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            code: err?.response?.status,
            path: url,
            message: finalErrorMessage,
            type: AlertTypes.API,
            method: method
          });
        }

        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return err;
      } finally {
        if (setIsFetching) setIsFetching(false);
      }
    },
    [createAlert]
  );

  return (
    <ApiContext.Provider value={{ execute }}>{children}</ApiContext.Provider>
  );
};
export const useApi = () => {
  const context = useContext(ApiContext);

  return context;
};
